/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import { ListProjectDto } from '@codefluegel/zeta-change-typescript-client';
import ChangeFilter from '../ChangeFilter/ChangeFilter';

interface ChangeRequestFiltersProps {
  searchString: string;
  setSearchString: (search: string) => void;
  filteredStatuses: string[];
  setFilteredStatuses: (statuses: string[]) => void;
  filteredProjects: string[];
  setFilteredProjects: (projects: string[]) => void;
  filteredChangeTypes: string[];
  setFilteredChangeTypes: (changeTypes: string[]) => void;
  projects: ListProjectDto[];
}

const ChangeRequestFilters = observer(
  ({
    searchString,
    setSearchString,
    filteredStatuses,
    setFilteredStatuses,
    filteredProjects,
    setFilteredProjects,
    filteredChangeTypes,
    setFilteredChangeTypes,
    projects,
  }: ChangeRequestFiltersProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    // Map projects to Autocomplete options
    const projectOptions = projects.map(project => ({
      value: project.id.toString(),
      label: `${project.number} - ${project.name}`,
    }));

    // Map change types to Autocomplete options
    const changeTypeOptions = [
      { value: 'internal', label: t('change.internalChange') },
      { value: 'external', label: t('change.externalChange') },
    ];

    // Get the currently selected project and change type objects
    const selectedProjects = projectOptions.filter(option =>
      filteredProjects.includes(option.value),
    );
    const selectedChangeTypes = changeTypeOptions.filter(option =>
      filteredChangeTypes.includes(option.value),
    );

    return (
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 0 16px;
          width: 100%;

          ${theme.breakpoints.up('md')} {
            flex-direction: row;
            align-items: center;
          }
        `}
      >
        <TextField
          onChange={e => setSearchString(e.target.value)}
          label={t('buttons.search')}
          id="outlined-basic"
          variant="outlined"
          sx={{ flex: 1, background: 'white' }}
          placeholder={t(t('change.searchText'))}
          value={searchString}
        />

        <ChangeFilter
          filteredStatuses={filteredStatuses}
          setFilteredStatuses={setFilteredStatuses}
          sx={{ flex: 1 }}
        />

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={projectOptions}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedProjects}
          onChange={(event, newValue) => {
            setFilteredProjects(newValue.map(option => option.value));
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={t('project.filterByProject')}
              sx={{ background: 'white' }}
              placeholder={t('project.selectProject')}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={
                  projects.find(p => p.id.toString() === option.value)
                    ?.number || option.label
                }
                size="small"
              />
            ))
          }
          sx={{ flex: 1 }}
        />

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={changeTypeOptions}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedChangeTypes}
          onChange={(event, newValue) => {
            setFilteredChangeTypes(newValue.map(option => option.value));
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={t('change.filterByChangeType')}
              sx={{ background: 'white' }}
              placeholder={t('change.changeTypePlaceholder')}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
              />
            ))
          }
          sx={{ flex: 1 }}
        />
      </Box>
    );
  },
);

export default ChangeRequestFilters;
